import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  SalaryByMonthResponse,
  SalaryByMonthItem,
  PayrollTestResponse,
  PayrollTestItem,
  PayrollReportResponse,
  PayrollReportItem,
  PayrollCompareResponse,
  PayrollCompareLine,
  PayrollCompareSnapshot,
  GeneratePayrollPayload,
  SalaryById,
  MissedPayrollEmployee,
  TestSalaryUpdatePayload,
} from '@/api/types/payroll.types'
import type { ApiResponse } from '@/api/types'
import { calendarToApi } from '@/lib/calendarDate'

/**
 * Convert YYYY-MM (input month) to MM-YYYY for API (e.g. 2026-02 -> 02-2026)
 */
export function toApiMonthYear(ym: string): string {
  if (!ym || ym.length < 7) return ym
  const [y, m] = ym.split('-')
  return `${m}-${y}`
}

/** MM-YYYY (API) -> YYYY-MM (month input) */
export function toInputMonthYear(ym: string): string {
  if (!ym || ym.length < 7) return ym
  const [a, b] = ym.split('-')
  if (a.length === 4) return ym
  return `${b}-${a}`
}

/**
 * GET GetSalaryByMonth – salary list by month/year
 * API expects monthYear as MM-YYYY (e.g. 02-2026)
 */
export async function getSalaryByMonth(
  branchId: number,
  monthYear: string,
  pageNumber = 1,
  pageSize = 50
): Promise<SalaryByMonthResponse> {
  const apiMonthYear = toApiMonthYear(monthYear)
  const url = endpoints.payroll.getSalaryByMonth(branchId, apiMonthYear, pageNumber, pageSize)
  const res = await apiClient.get<ApiResponse<SalaryByMonthResponse> | SalaryByMonthResponse>(url)
  const raw = res.data
  const arr: any[] =
    raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)
      ? (raw as any).data
      : raw && typeof raw === 'object' && Array.isArray((raw as any).data)
        ? (raw as any).data
        : []

  const mapItem = (item: any): SalaryByMonthItem => ({
    id: item.id ?? item.Id,
    empId: item.empId ?? item.EmpId ?? item.employeeId ?? item.EmployeeId,
    employeeId: item.employeeId ?? item.EmployeeId ?? item.empId ?? item.EmpId,
    employeeName: item.employeeName ?? item.EmployeeName,
    empCode: item.empCode ?? item.EmpCode,
    monthYear: item.monthYear ?? item.MonthYear ?? item.salaryMonthYear ?? item.SalaryMonthYear,
    salaryMonthYear: item.salaryMonthYear ?? item.SalaryMonthYear ?? item.monthYear ?? item.MonthYear,
    totalSalary: item.totalSalary ?? item.TotalSalary,
    payrollDate: item.payrollDate ?? item.PayrollDate ?? item.date ?? item.Date,
    // Allowances (API may use camelCase or PascalCase; note typo transpotation)
    basicSalary: item.basicSalary ?? item.BasicSalary,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance,
    transpotationAllowance: item.transpotationAllowance ?? item.TranspotationAllowance ?? item.transportationAllowance ?? item.TransportationAllowance,
    mealAllowance: item.mealAllowance ?? item.MealAllowance,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance,
    // Deductions
    gosiDeduction: item.gosiDeduction ?? item.GosiDeduction ?? item.gosi ?? item.Gosi,
    otAmount: item.otAmount ?? item.OtAmount ?? 0,
    loanDeduction: item.loanDeduction ?? item.LoanDeduction ?? 0,
    lateDeduction: item.lateDeduction ?? item.LateDeduction ?? 0,
    earlyOutDeduction: item.earlyOutDeduction ?? item.EarlyOutDeduction ?? 0,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction ?? 0,
    otherDeductions: item.otherDeductions ?? item.OtherDeductions ?? item.otherDeduction ?? item.OtherDeduction,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction,
    department: item.department ?? item.Department,
    project: item.project ?? item.Project,
    monthlyBonus: item.monthlyBonus ?? item.MonthlyBonus ?? item.bonus ?? item.Bonus ?? 0,
    monthlyIncentive: item.monthlyIncentive ?? item.MonthlyIncentive ?? item.incentive ?? item.Incentive ?? 0,
  })

  return {
    data: arr.map(mapItem),
    totalRecords: (raw as any)?.totalRecords ?? arr.length,
    pageNumber: (raw as any)?.pageNumber ?? pageNumber,
    pageSize: (raw as any)?.pageSize ?? pageSize,
  }
}

/**
 * GET GetReport – payroll report by fromMonth/toMonth
 * fromMonth, toMonth: YYYY-MM (converted to MM-YYYY for API)
 */
export async function getPayrollReport(
  branchId: number,
  fromMonth: string,
  toMonth: string,
  pageNumber = 1,
  pageSize = 50
): Promise<PayrollReportResponse> {
  const apiFrom = toApiMonthYear(fromMonth)
  const apiTo = toApiMonthYear(toMonth)
  const url = endpoints.payroll.getReport({ branchId, fromMonth: apiFrom, toMonth: apiTo, pageNumber, pageSize })
  const res = await apiClient.get<ApiResponse<PayrollReportResponse> | PayrollReportResponse>(url)
  const raw = res.data as any
  let arr: any[] = []
  if (Array.isArray(raw)) {
    arr = raw
  } else if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
    arr = raw.data
  }
  const totalRec = raw?.totalRecords ?? arr.length
  const pNum = raw?.pageNumber ?? pageNumber
  const pSize = raw?.pageSize ?? pageSize
  const mapItem = (item: any): PayrollReportItem => ({
    empId: item.empId ?? item.EmpId,
    empRef: item.empRef ?? item.EmpRef,
    empCode: item.empCode ?? item.EmpCode,
    employeeName: item.employeeName ?? item.EmployeeName,
    department: item.department ?? item.Department,
    projectRef: item.projectRef ?? item.ProjectRef,
    project: item.project ?? item.Project,
    monthYear: item.monthYear ?? item.MonthYear ?? null,
    contractSalary: item.contractSalary ?? item.ContractSalary ?? 0,
    basicSalary: item.basicSalary ?? item.BasicSalary ?? 0,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance ?? 0,
    transportationAllowance: item.transportationAllowance ?? item.TransportationAllowance ?? 0,
    mealAllowance: item.mealAllowance ?? item.MealAllowance ?? 0,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance ?? 0,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance ?? 0,
    grossSalary: item.grossSalary ?? item.GrossSalary ?? 0,
    bonus: item.bonus ?? item.Bonus ?? 0,
    incentive: item.incentive ?? item.Incentive ?? 0,
    otAmount: item.otAmount ?? item.OtAmount ?? 0,
    loanDeduction: item.loanDeduction ?? item.LoanDeduction ?? 0,
    lateDeduction: item.lateDeduction ?? item.LateDeduction ?? 0,
    earlyOutDeduction: item.earlyOutDeduction ?? item.EarlyOutDeduction ?? 0,
    absentDays: item.absentDays ?? item.AbsentDays ?? 0,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction ?? 0,
    gosi: item.gosi ?? item.Gosi ?? 0,
    otherDeduction: item.otherDeduction ?? item.OtherDeduction ?? 0,
    nonRecurringDeduction: item.nonRecurringDeduction ?? item.NonRecurringDeduction ?? 0,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction ?? 0,
    netSalary: item.netSalary ?? item.NetSalary ?? 0,
  })
  return {
    data: arr.map(mapItem),
    totalRecords: totalRec,
    pageNumber: pNum,
    pageSize: pSize,
  }
}

export async function getPayrollComparison(
  branchId: number,
  empId: number,
  monthYear1: string,
  monthYear2: string,
): Promise<PayrollCompareResponse> {
  const url = endpoints.payroll.getPayrollComparison({
    branchId,
    empId,
    monthYear1: toApiMonthYear(monthYear1),
    monthYear2: toApiMonthYear(monthYear2),
  })
  const res = await apiClient.get<ApiResponse<PayrollCompareResponse> | PayrollCompareResponse>(url)
  const raw: any = (res.data as any)?.data ?? res.data
  const num = (v: any) => Number(v ?? 0)
  const mapSnap = (s: any): PayrollCompareSnapshot => ({
    basicSalary: num(s?.basicSalary ?? s?.BasicSalary),
    housingAllowance: num(s?.housingAllowance ?? s?.HousingAllowance),
    transportationAllowance: num(s?.transportationAllowance ?? s?.TransportationAllowance),
    mealAllowance: num(s?.mealAllowance ?? s?.MealAllowance),
    otherAllowance: num(s?.otherAllowance ?? s?.OtherAllowance),
    totalAllowance: num(s?.totalAllowance ?? s?.TotalAllowance),
    bonus: num(s?.bonus ?? s?.Bonus),
    incentive: num(s?.incentive ?? s?.Incentive),
    otAmount: num(s?.otAmount ?? s?.OtAmount),
    grossPay: num(s?.grossPay ?? s?.GrossPay),
    gosi: num(s?.gosi ?? s?.Gosi),
    loanDeduction: num(s?.loanDeduction ?? s?.LoanDeduction),
    absentDeduction: num(s?.absentDeduction ?? s?.AbsentDeduction),
    lateDeduction: num(s?.lateDeduction ?? s?.LateDeduction),
    earlyOutDeduction: num(s?.earlyOutDeduction ?? s?.EarlyOutDeduction),
    otherDeductions: num(s?.otherDeductions ?? s?.OtherDeductions),
    totalDeduction: num(s?.totalDeduction ?? s?.TotalDeduction),
    netSalary: num(s?.netSalary ?? s?.NetSalary),
    salaryMonthYear: s?.salaryMonthYear ?? s?.SalaryMonthYear ?? null,
  })
  const lines: PayrollCompareLine[] = Array.isArray(raw?.lines ?? raw?.Lines)
    ? (raw.lines ?? raw.Lines).map((line: any) => ({
        key: String(line.key ?? line.Key ?? ''),
        amount1: num(line.amount1 ?? line.Amount1),
        amount2: num(line.amount2 ?? line.Amount2),
        difference: num(line.difference ?? line.Difference),
      }))
    : []

  return {
    empId: Number(raw?.empId ?? raw?.EmpId ?? empId),
    empCode: raw?.empCode ?? raw?.EmpCode ?? null,
    employeeName: raw?.employeeName ?? raw?.EmployeeName ?? null,
    monthYear1: raw?.monthYear1 ?? raw?.MonthYear1 ?? monthYear1,
    monthYear2: raw?.monthYear2 ?? raw?.MonthYear2 ?? monthYear2,
    payroll1Found: Boolean(raw?.payroll1Found ?? raw?.Payroll1Found),
    payroll2Found: Boolean(raw?.payroll2Found ?? raw?.Payroll2Found),
    payroll1: mapSnap(raw?.payroll1 ?? raw?.Payroll1),
    payroll2: mapSnap(raw?.payroll2 ?? raw?.Payroll2),
    lines,
  }
}

function throwIfDbError(data: any) {
  const result = data?.result ?? data?.Result
  if (result && (result.isError === true || result.IsError === true)) {
    const err: any = new Error(result.msg || result.Msg || 'Save failed')
    err.response = { data }
    throw err
  }
}

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data
  if (raw && typeof raw === 'object' && Array.isArray(raw.Data)) return raw.Data
  return []
}

function mapPayrollTestItem(item: any): PayrollTestItem {
  return {
    id: item.id ?? item.Id,
    empId: item.empId ?? item.EmpId,
    empRef: item.empRef ?? item.EmpRef,
    empCode: item.empCode ?? item.EmpCode,
    employeeName: item.employeeName ?? item.EmployeeName,
    department: item.department ?? item.Department,
    projectRef: item.projectRef ?? item.ProjectRef,
    project: item.project ?? item.Project,
    monthYear: item.monthYear ?? item.MonthYear,
    contractSalary: item.contractSalary ?? item.ContractSalary,
    basicSalary: item.basicSalary ?? item.BasicSalary,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance,
    transportationAllowance: item.transportationAllowance ?? item.TransportationAllowance,
    mealAllowance: item.mealAllowance ?? item.MealAllowance,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance,
    grossSalary: item.grossSalary ?? item.GrossSalary,
    bonus: item.bonus ?? item.Bonus,
    incentive: item.incentive ?? item.Incentive,
    otAmount: item.otAmount ?? item.OtAmount ?? 0,
    loanDeduction: item.loanDeduction ?? item.LoanDeduction,
    lateLeaveDays: item.lateLeaveDays ?? item.LateLeaveDays ?? 0,
    lateDeduction: item.lateDeduction ?? item.LateDeduction ?? 0,
    earlyOutDeduction: item.earlyOutDeduction ?? item.EarlyOutDeduction ?? 0,
    absentDays: item.absentDays ?? item.AbsentDays ?? 0,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction ?? 0,
    gosi: item.gosi ?? item.Gosi,
    otherDeduction: item.otherDeduction ?? item.OtherDeduction ?? item.otherDeductions ?? item.OtherDeductions,
    nonRecurringDeduction: item.nonRecurringDeduction ?? item.NonRecurringDeduction,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction,
    netSalary: item.netSalary ?? item.NetSalary,
  }
}

function toPayrollTestResponse(raw: any, pageNumber: number, pageSize: number): PayrollTestResponse {
  const arr = extractList(raw)
  return {
    data: arr.map(mapPayrollTestItem),
    totalRecords: raw?.totalRecords ?? raw?.TotalRecords ?? arr.length,
    pageNumber: raw?.pageNumber ?? raw?.PageNumber ?? pageNumber,
    pageSize: raw?.pageSize ?? raw?.PageSize ?? pageSize,
  }
}

/**
 * GET GetTest – test payroll (detailed list). Pass save=true to persist/replace TestSalary.
 * startDate, endDate: YYYY-MM-DD or ISO date string
 */
export async function getPayrollTest(params: {
  branchId: number
  monthYear: string
  startDate: string
  endDate: string
  empId?: number
  pageNumber?: number
  pageSize?: number
  save?: boolean
}): Promise<PayrollTestResponse> {
  const { branchId, monthYear, startDate, endDate, empId = 0, pageNumber = 1, pageSize = 50, save = false } = params
  const apiMonthYear = toApiMonthYear(monthYear)
  const url = endpoints.payroll.getTest({
    monthYear: apiMonthYear,
    startDate: calendarToApi(startDate),
    endDate: calendarToApi(endDate),
    branchId,
    empId,
    pageNumber,
    pageSize,
    save,
  })
  const res = await apiClient.get<ApiResponse<PayrollTestResponse> | PayrollTestResponse>(url)
  return toPayrollTestResponse(res.data, pageNumber, pageSize)
}

export async function getTestSalary(branchId: number, monthYear: string): Promise<PayrollTestResponse> {
  const url = endpoints.payroll.getTestSalary(branchId, toApiMonthYear(monthYear))
  const res = await apiClient.get<ApiResponse<PayrollTestResponse> | PayrollTestResponse>(url)
  return toPayrollTestResponse(res.data, 1, 10000)
}

export async function updateTestSalary(payload: TestSalaryUpdatePayload): Promise<PayrollTestItem> {
  const res = await apiClient.put(endpoints.payroll.updateTestSalary, payload)
  throwIfDbError(res.data)
  const item = (res.data as any)?.data ?? (res.data as any)?.Data ?? res.data
  return mapPayrollTestItem(item)
}

/**
 * GET GetSalaryById – fetch single salary/pay slip by id
 */
export async function getSalaryById(id: number): Promise<SalaryById> {
  const url = endpoints.payroll.getSalaryById(id)
  const res = await apiClient.get<SalaryById>(url)
  const raw: any = res.data
  if (raw && typeof raw === 'object') {
    return {
      ...raw,
      contractSalary: raw.contractSalary ?? raw.ContractSalary ?? 0,
      payDays: raw.payDays ?? raw.PayDays ?? 30,
    } as SalaryById
  }
  return raw as SalaryById
}

/**
 * POST GeneratePayroll – generate/post payroll
 * monthYear: YYYY-MM; startDate/endDate: ISO or YYYY-MM-DD
 */
export async function generatePayroll(payload: GeneratePayrollPayload): Promise<unknown> {
  const url = endpoints.payroll.generatePayroll
  const res = await apiClient.post(url, payload)
  return res.data
}

export async function getMissedPayrollEmployees(params: {
  branchId: number
  departmentId: number
  monthYear: string
  startDate: string
  endDate: string
}): Promise<MissedPayrollEmployee[]> {
  const url = endpoints.payroll.getMissedEmployees({
    branchId: params.branchId,
    departmentId: params.departmentId,
    monthYear: toApiMonthYear(params.monthYear),
    startDate: calendarToApi(params.startDate),
    endDate: calendarToApi(params.endDate),
  })
  const res = await apiClient.get(url)
  const raw = res.data as any
  const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
  if (!Array.isArray(arr)) return []
  return arr.map((item: any): MissedPayrollEmployee => ({
    id: Number(item.id ?? item.Id ?? 0),
    empCode: item.empCode ?? item.EmpCode,
    empFirstNameEn: item.empFirstNameEn ?? item.EmpFirstNameEn,
    empFatherNameEn: item.empFatherNameEn ?? item.EmpFatherNameEn,
    empFamilyNameEn: item.empFamilyNameEn ?? item.EmpFamilyNameEn,
    empDepartmetId: item.empDepartmetId ?? item.EmpDepartmetId,
    employeeName: item.employeeName ?? item.EmployeeName,
  })).filter((e) => e.id > 0)
}
